import { forwardRef, useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import IconCheck from "../icons/icon-check";

interface FontSelectOption {
	value: string;
	label: string;
}

interface FontSelectProps {
	label?: string;
	options: FontSelectOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: string;
	className?: string;
	variant?: "light" | "dark";
	id?: string;
}

const ITEM_HEIGHT = 36;
const OVERSCAN = 5;

// Track loaded fonts to avoid duplicate loading
const loadedFonts = new Set<string>();

function loadGoogleFont(fontName: string) {
	if (loadedFonts.has(fontName)) return;

	const linkId = `google-font-preview-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
	if (document.getElementById(linkId)) {
		loadedFonts.add(fontName);
		return;
	}

	const link = document.createElement("link");
	link.id = linkId;
	link.rel = "stylesheet";
	link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400&display=swap`;
	document.head.appendChild(link);
	loadedFonts.add(fontName);
}

const FontSelect = forwardRef<HTMLButtonElement, FontSelectProps>(
	({ label, options, value, onChange, placeholder = "Select font...", disabled = false, error, className = "", variant = "light", id }, ref) => {
		const [open, setOpen] = useState(false);
		const [search, setSearch] = useState("");
		const [highlightedIndex, setHighlightedIndex] = useState(0);
		const [scrollTop, setScrollTop] = useState(0);
		const searchInputRef = useRef<HTMLInputElement>(null);
		const listRef = useRef<HTMLDivElement>(null);
		const isDark = variant === "dark";
		const containerHeight = 256; // max-h-64 = 16rem = 256px

		const selectedOption = options.find((opt) => opt.value === value);

		// Filter options based on search
		const filteredOptions = useMemo(() => {
			if (!search.trim()) return options;
			const searchLower = search.toLowerCase();
			return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
		}, [options, search]);

		// Virtualization: compute visible window
		const { visibleItems, totalHeight } = useMemo(() => {
			const total = filteredOptions.length;
			const totalH = total * ITEM_HEIGHT;
			const start = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
			const visibleCount = Math.ceil(containerHeight / ITEM_HEIGHT) + OVERSCAN * 2;
			const end = Math.min(total, start + visibleCount);
			const items = filteredOptions.slice(start, end).map((option, i) => ({
				option,
				index: start + i,
			}));
			return { visibleItems: items, startIndex: start, totalHeight: totalH };
		}, [filteredOptions, scrollTop, containerHeight]);

		// Reset highlight when filtered options change
		useEffect(() => {
			setHighlightedIndex(0);
			setScrollTop(0);
			if (listRef.current) {
				listRef.current.scrollTop = 0;
			}
		}, [filteredOptions.length]);

		// Focus search input when popover opens
		useEffect(() => {
			if (open) {
				setTimeout(() => {
					searchInputRef.current?.focus();
				}, 10);
			} else {
				setSearch("");
				setHighlightedIndex(0);
				setScrollTop(0);
			}
		}, [open]);

		// Load fonts for visible options
		useEffect(() => {
			if (open) {
				visibleItems.forEach(({ option }) => {
					loadGoogleFont(option.value);
				});
			}
		}, [open, visibleItems]);

		// Scroll highlighted item into view
		useEffect(() => {
			if (open && listRef.current) {
				const itemTop = highlightedIndex * ITEM_HEIGHT;
				const itemBottom = itemTop + ITEM_HEIGHT;
				const viewTop = listRef.current.scrollTop;
				const viewBottom = viewTop + containerHeight;

				if (itemTop < viewTop) {
					listRef.current.scrollTop = itemTop;
				} else if (itemBottom > viewBottom) {
					listRef.current.scrollTop = itemBottom - containerHeight;
				}
			}
		}, [highlightedIndex, open]);

		const handleSelect = (optionValue: string) => {
			if (disabled) return;
			onChange(optionValue);
			setOpen(false);
		};

		const handleKeyDown = (e: React.KeyboardEvent) => {
			switch (e.key) {
				case "ArrowDown":
					e.preventDefault();
					setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
					break;
				case "ArrowUp":
					e.preventDefault();
					setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
					break;
				case "Enter":
					e.preventDefault();
					if (filteredOptions[highlightedIndex]) {
						handleSelect(filteredOptions[highlightedIndex].value);
					}
					break;
				case "Escape":
					e.preventDefault();
					setOpen(false);
					break;
			}
		};

		const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
			setScrollTop((e.target as HTMLDivElement).scrollTop);
		}, []);

		return (
			<div className={className}>
				{label && (
					<label htmlFor={id} className="block text-sm font-medium text-primary mb-1.5">
						{label}
					</label>
				)}
				<Popover.Root open={disabled ? false : open} onOpenChange={(isOpen: boolean) => !disabled && setOpen(isOpen)}>
					<Popover.Trigger asChild>
						<button
							ref={ref}
							id={id}
							type="button"
							disabled={disabled}
							className={`flex items-center justify-between w-full px-4 py-2 border rounded-lg text-sm font-sans transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
								isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
							} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${error ? "border-red-500" : ""} focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1`}
						>
							<span
								className={`flex-1 min-w-0 truncate text-left ${selectedOption ? (isDark ? "text-white" : "text-primary") : isDark ? "text-gray-500" : "text-gray-400"}`}
								style={selectedOption ? { fontFamily: `"${selectedOption.value}", sans-serif` } : undefined}
							>
								{selectedOption ? selectedOption.label : placeholder}
							</span>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className={`shrink-0 ml-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
								<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</Popover.Trigger>
					<Popover.Portal>
						<Popover.Content
							className={`rounded-lg shadow-lg border z-50 w-[var(--radix-popover-trigger-width)] ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
							sideOffset={4}
							align="start"
							onKeyDown={handleKeyDown}
						>
							{/* Search input */}
							<div className={`p-2 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
								<div className="relative">
									<svg
										className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={2}
									>
										<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
									<input
										ref={searchInputRef}
										type="text"
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder="Search fonts..."
										className={`w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none transition-colors ${
											isDark
												? "bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-gray-600"
												: "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"
										}`}
									/>
								</div>
							</div>

							{/* Virtualized options list */}
							<div ref={listRef} className="max-h-64 overflow-y-auto" onScroll={handleScroll}>
								{filteredOptions.length === 0 ? (
									<div className={`px-4 py-3 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>No fonts found</div>
								) : (
									<div style={{ height: totalHeight, position: "relative" }}>
										{visibleItems.map(({ option, index }) => (
											<button
												key={option.value}
												type="button"
												data-index={index}
												onClick={() => handleSelect(option.value)}
												onMouseEnter={() => {
													setHighlightedIndex(index);
													loadGoogleFont(option.value);
												}}
												className={`flex items-center justify-between w-full px-4 text-sm cursor-pointer outline-none transition-colors ${
													index === highlightedIndex ? (isDark ? "bg-gray-700" : "bg-gray-100") : isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
												}`}
												style={{
													position: "absolute",
													top: index * ITEM_HEIGHT,
													height: ITEM_HEIGHT,
													left: 0,
													right: 0,
												}}
											>
												<span className={isDark ? "text-white" : "text-primary"} style={{ fontFamily: `"${option.value}", sans-serif` }}>
													{option.label}
												</span>
												{value === option.value && (
													<span className="ml-2">
														<IconCheck className="w-4 h-4 text-brand" />
													</span>
												)}
											</button>
										))}
									</div>
								)}
							</div>

							{/* Footer with count */}
							{filteredOptions.length > 0 && (
								<div className={`px-3 py-2 border-t text-xs ${isDark ? "border-gray-700 text-gray-500" : "border-gray-200 text-gray-400"}`}>
									{filteredOptions.length} {filteredOptions.length === 1 ? "font" : "fonts"}
									{search && ` matching "${search}"`}
								</div>
							)}
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
				{error && <p className="text-sm text-red-500 mt-1">{error}</p>}
			</div>
		);
	},
);

FontSelect.displayName = "FontSelect";

export default FontSelect;
